"use client";

import { CircleCheck as CheckCircle2, ClipboardCheck, Compass, FileCheck, Hammer, MessagesSquare } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface StepItem {
  number: string;
  title: string;
  phase: string;
  description: string;
  icon: typeof MessagesSquare;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const STEPS: StepItem[] = [{
  number: "01",
  phase: "Initial Alignment",
  title: "In-home basement consultation",
  description: "We walk your basement together to evaluate foundation walls, ceiling heights, moisture levels, plumbing lines, and structural elements. We listen to how you plan to use the space and outline feasible scope and investment ranges.",
  icon: MessagesSquare,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_consultation_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_consultation_walkthrough.png-wvc-srcset",
    sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw",
    alt: "Contractor and homeowner discussing architectural basement plans on an open framing floor"
  }
}, {
  number: "02",
  phase: "Architectural Planning",
  title: "Custom design and material planning",
  description: "Our team translates your functional goals into detailed layout drawings, egress window locations, lighting schedules, and HVAC routing. You finalize cabinetry, moisture-resistant flooring, and finish selections before construction starts.",
  icon: Compass,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_architectural_design_blueprints.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_architectural_design_blueprints.webp-wvc-srcset",
    sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw",
    alt: "Detailed architectural blueprint and material samples for a finished lower level renovation"
  }
}, {
  number: "03",
  phase: "Municipal Compliance",
  title: "Permitting and code compliance",
  description: "We handle every permit application, engineering review, and municipal filing required in Brookhaven and surrounding jurisdictions. Egress safety, electrical load plans, and plumbing rough-in documents are fully approved prior to work.",
  icon: FileCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_permitting_plan_review.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_permitting_plan_review.png-wvc-srcset",
    sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw",
    alt: "Approved municipal building permit documents laid out at a residential construction jobsite"
  }
}, {
  number: "04",
  phase: "Craft and Execution",
  title: "Structured basement construction",
  description: "Dedicated trades proceed in clean phases: thermal moisture barrier installation, framing, electrical and plumbing rough-ins, drywall, waterproof subfloors, trim carpentry, and custom wet bar or media center cabinetry.",
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_framing_drywall_construction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_framing_drywall_construction.png-wvc-srcset",
    sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw",
    alt: "Clean framing, mechanical rough-ins, and drywall installation during a luxury basement build"
  }
}, {
  number: "05",
  phase: "Final Sign-off",
  title: "Detailed final walkthrough",
  description: "We perform our comprehensive multi-point inspection, review mechanical systems and warranty packets, test fixtures, and complete every final detail to your satisfaction before handing over your finished living space.",
  icon: ClipboardCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_basement_final_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_basement_final_walkthrough.png-wvc-srcset",
    sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw",
    alt: "Polished finished basement recreation room with custom bar, recessed lighting, and media lounge"
  }
}];
export default function RemodelingProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="remodelingprocess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={headerRef} className={`max-w-3xl mb-14 md:mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={!headerVisible ? {
        opacity: 0
      } : undefined}>
          <Badge variant="outline" className="mb-4 text-xs font-semibold tracking-wide border-primary/40 text-foreground">
            Our 5-step process
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground text-balance">
            A clear path from concrete foundation to finished retreat.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Basement renovations demand exacting waterproofing, structural know-how, and disciplined trade coordination.
            We guide your project through five transparent stages so you always know what comes next.
          </p>
        </div>

        {/* Steps Grid */}
        <div ref={listRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((step, i) => {
          const Icon = step.icon;
          return <div key={step.number} data-index={i} className={`transition-all duration-700 ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={!listVisible ? {
            opacity: 0
          } : undefined}>
                <Card className="h-full border border-border/80 bg-card hover:border-primary/50 transition-colors shadow-xs hover:shadow-md flex flex-col justify-between overflow-hidden">
                  <div>
                    {/* Realistic Basement Photo Frame */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-3 left-3 bg-secondary/90 backdrop-blur-xs text-secondary-foreground font-mono text-xs px-2.5 py-1 rounded-sm tracking-wider font-semibold border border-border/20">
                        STEP {step.number}
                      </div>
                    </div>

                    <CardContent className="pt-6 pb-2">
                      <div className="flex items-center gap-2.5 text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">
                        <Icon className="size-4 text-primary" />
                        <span>{step.phase}</span>
                      </div>

                      <h3 className="font-serif text-xl font-medium text-foreground tracking-tight mb-3">
                        {step.title}
                      </h3>

                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-3 mt-auto">
                    <div className="flex items-center gap-2 pt-4 border-t border-border/70 text-xs font-medium text-foreground/80">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>Documented milestone review</span>
                    </div>
                  </div>
                </Card>
              </div>;
        })}

          {/* Call-to-Action Card completing the 6-slot grid */}
          <div data-index={5} className={`transition-all duration-700 ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={!listVisible ? {
          opacity: 0
        } : undefined}>
            <Card className="h-full border border-primary/30 bg-secondary text-secondary-foreground flex flex-col justify-between p-6 sm:p-8 shadow-md">
              <div className="space-y-4">
                <span className="font-mono text-xs tracking-widest text-primary uppercase font-medium">
                  Ready to begin
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl font-medium tracking-tight text-secondary-foreground">
                  Start with a complimentary site evaluation.
                </h3>
                <p className="text-sm text-secondary-foreground/85 leading-relaxed">
                  Have Brookhaven Elevation Renovations inspect your lower level, answer permit questions, and provide a clear, realistic cost estimate.
                </p>
              </div>

              <div className="pt-8">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an estimate
                  </Link>
                </Button>
                <p className="mt-3 text-center text-xs text-secondary-foreground/70">
                  Serving Brookhaven and surrounding communities
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}